// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * ADR-058 (founder bug 2, 2026-09-05) — safe retry semantics for an Amy turn
 * whose AgentCore stream fails. Twin module: frontend/src/lib and
 * mobile/src/lib carry the byte-identical file (pinned by the mobile test).
 *
 * Owner decision 5 (verbatim): "After any tool/write/generative side effect
 * has occurred, the web/mobile client must NOT blindly resend the entire
 * user turn. Preserve partial assistant output where possible and show
 * truthful interruption/recovery UX. A retry must never duplicate writes,
 * composed recipes, proposals, logged meals or other side effects."
 *
 * The rule is deliberately tiny and pure so both clients decide identically:
 * a transparent retry (fresh runtime session + the same turn again) is safe
 * ONLY when the failed attempt was the first one AND nothing came back — no
 * model output, no tool activity. Once the model has started answering or a
 * tool has run, the turn may already have had side effects (the founder saw
 * three orphan recipes from a resent compose loop), so the failure is
 * reported as an interruption: the partial reply stays, the notice tells the
 * user to check Today and Plan before asking again, and nothing is resent.
 */

/** What the failed attempt streamed back before it broke. */
export interface AttemptProgress {
  /** The model produced output — a streamed text delta, a complete message of the agent loop, or its final result. */
  receivedText: boolean
  /** At least one tool event arrived — a tool started, streamed its input, or finished. */
  sawToolActivity: boolean
}

export function emptyAttemptProgress(): AttemptProgress {
  return { receivedText: false, sawToolActivity: false }
}

/**
 * Every `StreamEvent["type"]` of the shared AgentCore client is classified
 * here, deliberately (the source contracts pin the three sets to the union):
 * model output, tool activity, or a runtime lifecycle marker ("init",
 * "start", a session id) after which nothing was produced yet — the classic
 * cold-start / deploy-killed case where the transparent retry is still safe.
 * A `message` or `result` event means the agent loop ran the turn: resending
 * it would run it again.
 */
export const MODEL_OUTPUT_EVENT_TYPES: ReadonlySet<string> = new Set(["text", "message", "result"])
export const TOOL_EVENT_TYPES: ReadonlySet<string> = new Set([
  "tool_use_start",
  "tool_use_delta",
  "tool_result",
])
export const NON_OUTPUT_EVENT_TYPES: ReadonlySet<string> = new Set(["lifecycle"])

/** The progress after one stream event; a lifecycle marker or an unknown event type changes nothing. */
export function progressAfterEvent(progress: AttemptProgress, eventType: string): AttemptProgress {
  if (MODEL_OUTPUT_EVENT_TYPES.has(eventType))
    return progress.receivedText ? progress : { ...progress, receivedText: true }
  if (TOOL_EVENT_TYPES.has(eventType))
    return progress.sawToolActivity ? progress : { ...progress, sawToolActivity: true }
  return progress
}

// ADR-079: the retry decision moved to lib/amyTurn.ts (`decideAfterStreamLoss`) —
// it now needs the SERVER's status for the turn, which this module never sees.

/**
 * How the final failure of a turn is reported: "interrupted" when something
 * came back (model output and/or a tool ran) — the partial reply is
 * preserved and the interruption notice explains the state truthfully;
 * "failed" when nothing ever came back, even after the retry — the generic
 * error copy, unchanged.
 */
export type FailureKind = "interrupted" | "failed"

export function failureKind(progress: AttemptProgress): FailureKind {
  return progress.receivedText || progress.sawToolActivity ? "interrupted" : "failed"
}

export interface FailureCopy {
  /** `amy.interrupted` */
  interrupted: string
  /** `amy.error` — the owner keeps this fallback for the nothing-came-back case. */
  error: string
}

/**
 * The assistant bubble after the final failure: the partial reply, when
 * there is one, with the interruption notice beneath it (a paragraph apart);
 * the notice alone when the turn ran but no text arrived; the generic error
 * copy when nothing came back at all. Partial output is never discarded.
 */
export function bubbleAfterFailure(
  partialText: string,
  progress: AttemptProgress,
  copy: FailureCopy
): string {
  const partial = partialText.trim() === "" ? "" : partialText.trimEnd()
  if (partial !== "") return `${partial}\n\n${copy.interrupted}`
  return failureKind(progress) === "interrupted" ? copy.interrupted : copy.error
}
